import React, { Component } from 'react';
import { observable, action, computed, autorun } from 'mobx';
import { observer, inject } from 'mobx-react';
import { toast } from 'react-toastify';
import {
  Modal, ModalHeader, ModalBody,
  FormInput, FormTextarea, FormCheckbox, Button, Row, Col
} from 'shards-react';
import FeatherIcon from 'feather-icons-react';

const TYPE_LABELS = {
  noPaied: '미결제',
  noPeriod: '기간 만료',
  event: '이벤트',
  planUpgrade: '업그레이드',
  planDowngrade: '다운그레이드',
};

const DEFAULT_SMS_SENDER = '0264177000';

@inject(({ AOA }) => ({
  getNotificationPreview: AOA.getNotificationPreview,
  sendMailTo: AOA.sendMailTo,
  sendAligoSms: AOA.sendAligoSms,
}))
@observer
class NotificationSendModal extends Component {

  @observable isLoading = false;
  @observable isSending = false;

  @observable preview = null;    // backend response

  @observable sendEmail = true;
  @observable sendSms = false;

  // 편집 가능한 필드
  @observable emailTo = '';
  @observable emailSubject = '';
  @observable emailHtml = '';

  @observable smsTo = '';
  @observable smsSender = DEFAULT_SMS_SENDER;
  @observable smsMessage = '';
  @observable smsMsgType = 'AUTO';

  // event 입력
  @observable eventTitle = '';
  @observable eventBody = '';

  @observable result = { email: null, sms: null };
  @observable lastParamsKey = null;  // open 시점의 식별자 — props 변경 감지

  // 발송 전 내용 확인 체크
  @observable confirmEmail = false;
  @observable confirmSms = false;

  PERSIST_KEY = 'notifySend_draft';

  componentDidMount() {
    // 탭 전환/리마운트 시 작성 중 상태 복원 (모달이 열려있는 경우)
    try {
      const saved = window.sessionStorage && window.sessionStorage.getItem(this.PERSIST_KEY);
      if (saved && this.props.isOpen) {
        const parsed = JSON.parse(saved);
        if (parsed) {
          ['emailTo','emailSubject','emailHtml','smsTo','smsSender','smsMessage','smsMsgType','eventTitle','eventBody','sendEmail','sendSms','confirmEmail','confirmSms'].forEach(k => {
            if (parsed[k] !== undefined) this[k] = parsed[k];
          });
          if (parsed.preview) this.preview = parsed.preview;
        }
      }
    } catch (e) {}
    this._persistDisposer = autorun(() => {
      try {
        if (this.props.isOpen && this.preview) {
          window.sessionStorage && window.sessionStorage.setItem(this.PERSIST_KEY, JSON.stringify({
            emailTo: this.emailTo, emailSubject: this.emailSubject, emailHtml: this.emailHtml,
            smsTo: this.smsTo, smsSender: this.smsSender, smsMessage: this.smsMessage, smsMsgType: this.smsMsgType,
            eventTitle: this.eventTitle, eventBody: this.eventBody,
            sendEmail: this.sendEmail, sendSms: this.sendSms,
            confirmEmail: this.confirmEmail, confirmSms: this.confirmSms,
            preview: this.preview,
          }));
        }
      } catch (e) {}
    });
  }

  componentWillUnmount() {
    if (this._persistDisposer) { try { this._persistDisposer(); } catch (e) {} }
  }

  componentDidUpdate(prevProps) {
    // props.params가 새로 들어오면 미리보기 재조회
    const prev = prevProps.params;
    const cur = this.props.params;
    if (this.props.isOpen && cur && (!prev || prev !== cur)) {
      this.resetAndFetch();
    }
    // 모달이 닫히면 sessionStorage 정리
    if (!this.props.isOpen && prevProps.isOpen) {
      try { window.sessionStorage && window.sessionStorage.removeItem(this.PERSIST_KEY); } catch (e) {}
    }
  }

  @action resetAndFetch = () => {
    this.preview = null;
    this.result = { email: null, sms: null };
    this.sendEmail = true;
    this.sendSms = false;
    this.eventTitle = '';
    this.eventBody = '';
    this.smsSender = DEFAULT_SMS_SENDER;
    this.smsMsgType = 'AUTO';
    this.confirmEmail = false;
    this.confirmSms = false;
    this.fetchPreview();
  };

  close = () => {
    if (this.isSending) return;
    if (this.props.onClose) this.props.onClose();
  };

  @action fetchPreview = async () => {
    if (!this.props.params) return;
    const { item, type, prevPlan } = this.props.params;
    this.isLoading = true;
    try {
      const args = {
        id: item.store_id ? item.store_id._id : item._id,
        shopNo: item.platformShopNo || item.shopNo || 1,
        type,
        prevPlan: prevPlan || '',
      };
      if (type === 'event') {
        args.eventTitle = this.eventTitle || '';
        args.eventBody = this.eventBody || '';
      }
      const res = await this.props.getNotificationPreview(args);
      if (res && res.data && res.data.error) {
        toast.error(res.data.error);
      } else if (res && res.data) {
        this.preview = res.data;
        this.emailTo = res.data.email.to || '';
        this.emailSubject = res.data.email.subject || '';
        this.emailHtml = res.data.email.html || '';
        this.smsTo = res.data.sms.to || '';
        this.smsMessage = res.data.sms.message || '';
        // SMS 자동 활성: 모바일이고 메시지가 있을 때
        this.sendSms = !!res.data.sms.isMobile && !!res.data.sms.message;
      }
    } catch (e) {
      toast.error('미리보기 로드 실패');
    } finally {
      this.isLoading = false;
    }
  };

  @computed get smsLength() { return (this.smsMessage || '').length; }
  @computed get smsAutoType() {
    if (this.smsMsgType !== 'AUTO') return this.smsMsgType;
    return this.smsLength > 90 ? 'LMS' : 'SMS';
  }

  @computed get canSubmit() {
    if (this.isSending) return false;
    if (!this.preview) return false;
    if (!this.sendEmail && !this.sendSms) return false;
    if (this.sendEmail && !this.confirmEmail) return false;
    if (this.sendSms && !this.confirmSms) return false;
    return true;
  }

  @action onChangeEvent = (key, value) => {
    this[key] = value;
  };

  @action regeneratePreview = () => {
    // 이벤트 타이틀/본문 변경 후 다시 빌드
    this.fetchPreview();
  };

  @action onSubmit = async () => {
    if (!this.preview) return;
    if (!this.sendEmail && !this.sendSms) {
      toast.warn('이메일 또는 문자 중 최소 하나를 선택해 주세요.');
      return;
    }
    if (this.sendEmail && !this.emailTo) {
      toast.warn('이메일 수신 주소가 비어있습니다.');
      return;
    }
    if (this.sendSms) {
      if (!this.smsTo) {
        toast.warn('수신 전화번호가 비어있습니다.');
        return;
      }
      if (!/^(010\d{7,8}|01[16-9]\d{7,8})$/.test(this.smsTo)) {
        toast.warn('수신 번호가 모바일 번호 형식이 아닙니다.');
        return;
      }
      if (!this.smsMessage) {
        toast.warn('문자 내용을 입력해 주세요.');
        return;
      }
    }

    const channels = [];
    if (this.sendEmail) channels.push('이메일');
    if (this.sendSms) channels.push('문자');
    if (!window.confirm(`${this.preview.store.name || '스토어'}에 ${channels.join(' / ')} 알림을 발송합니다. 계속하시겠습니까?`)) return;

    this.isSending = true;
    this.result = { email: null, sms: null };

    const params = this.props.params;

    // 이메일 발송 (현재 sendMailTo는 백엔드에서 자체 빌드 — subject/html을 무시함. 그대로 호출)
    if (this.sendEmail) {
      try {
        const args = {
          id: params.item.store_id ? params.item.store_id._id : params.item._id,
          shopNo: params.item.platformShopNo || params.item.shopNo || 1,
          type: params.type,
        };
        if (params.prevPlan) args.prevPlan = params.prevPlan;
        const res = await this.props.sendMailTo(args);
        if (res && res.errors && res.errors.length > 0) {
          this.result.email = { ok: false, error: res.errors[0].message };
          toast.error('이메일 발송 실패: ' + res.errors[0].message);
        } else {
          this.result.email = { ok: true };
          toast.success('이메일 발송 완료');
        }
      } catch (e) {
        this.result.email = { ok: false, error: e.message || String(e) };
        toast.error('이메일 발송 중 오류');
      }
    }

    // 문자 발송
    if (this.sendSms) {
      try {
        const res = await this.props.sendAligoSms({
          sender: this.smsSender,
          receivers: [this.smsTo],
          message: this.smsMessage,
          msgType: this.smsMsgType === 'AUTO' ? null : this.smsMsgType,
          sourceLabel: 'shopListNotification:' + params.type,
        });
        if (res && res.data && res.data.error) {
          this.result.sms = { ok: false, error: res.data.error };
          toast.error('문자 발송 실패: ' + res.data.error);
        } else if (res && res.data) {
          const apiResult = res.data.apiResult || {};
          if (apiResult.result_code !== undefined && Number(apiResult.result_code) < 0) {
            this.result.sms = { ok: false, error: apiResult.message || '알리고 응답 오류' };
            toast.error('문자 발송 실패: ' + (apiResult.message || ''));
          } else {
            this.result.sms = { ok: true, msgId: apiResult.msg_id };
            toast.success('문자 발송 완료');
          }
        }
      } catch (e) {
        this.result.sms = { ok: false, error: e.message || String(e) };
        toast.error('문자 발송 중 오류');
      }
    }

    this.isSending = false;
  };

  render() {
    const { isOpen, params } = this.props;
    if (!isOpen || !params) return null;
    const p = this.preview;
    const typeLabel = TYPE_LABELS[params.type] || params.type;
    const isEvent = params.type === 'event';

    return (
      <>
      <style>{`
        .modal-dialog.notify-send-modal { max-width: 1200px !important; width: 95vw; }
        .notify-send-modal .modal-body { max-height: 82vh; overflow-y: auto; }
      `}</style>
      <Modal size="lg" className="notify-send-modal" open={isOpen} toggle={() => {}} centered>
        <ModalHeader>
          <FeatherIcon icon="bell" size="16" style={{verticalAlign: '-3px', marginRight: 6}}/>
          {typeLabel} 알림 발송
        </ModalHeader>
        <ModalBody>
          {this.isLoading && <div className="text-center p-4">미리보기 로드 중…</div>}
          {!this.isLoading && p && (
            <>
              <div style={{padding: '8px 12px', background: '#F3F4F6', borderRadius: 6, marginBottom: 14, fontSize: 13}}>
                <strong>{p.store.name || '-'}</strong>
                {p.store.presidentName && <span style={{marginLeft: 8, color: '#6B7280'}}>대표자: {p.store.presidentName}</span>}
                <span style={{marginLeft: 8, color: '#6B7280'}}>이메일: {p.store.email || '-'}</span>
                <span style={{marginLeft: 8, color: '#6B7280'}}>전화: {p.store.tel || '-'}</span>
              </div>

              {isEvent && (
                <Row className="mb-3">
                  <Col md="12">
                    <label style={{fontSize: 12, fontWeight: 600}}>이벤트 제목</label>
                    <FormInput value={this.eventTitle}
                      onChange={e => this.onChangeEvent('eventTitle', e.target.value)}
                      onBlur={() => this.regeneratePreview()}
                      placeholder="예) 5월 신규 고객 이벤트"/>
                  </Col>
                  <Col md="12" className="mt-2">
                    <label style={{fontSize: 12, fontWeight: 600}}>이벤트 본문 (이메일/SMS 공통)</label>
                    <FormTextarea rows="3"
                      value={this.eventBody}
                      onChange={e => this.onChangeEvent('eventBody', e.target.value)}
                      onBlur={() => this.regeneratePreview()}/>
                    <small style={{color: '#6B7280'}}>입력 후 미리보기 갱신: 입력칸을 벗어나면 자동 새로고침</small>
                  </Col>
                </Row>
              )}

              {/* === 이메일 === */}
              <div style={{border: '1px solid #E5E7EB', borderRadius: 6, padding: 12, marginBottom: 12, background: this.sendEmail ? '#FFFFFF' : '#F9FAFB'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10}}>
                  <FormCheckbox checked={this.sendEmail} onChange={() => { this.sendEmail = !this.sendEmail; }}>
                    <strong>이메일 발송</strong>
                  </FormCheckbox>
                  {this.result.email && (
                    <span style={{marginLeft: 'auto', fontSize: 12, color: this.result.email.ok ? '#10B981' : '#DC2626'}}>
                      {this.result.email.ok ? '✓ 발송 완료' : '✕ ' + this.result.email.error}
                    </span>
                  )}
                </div>
                {this.sendEmail && (
                  <>
                    <Row>
                      <Col md="6">
                        <label style={{fontSize: 12, fontWeight: 600}}>받는 사람</label>
                        <FormInput value={this.emailTo} onChange={e => { this.emailTo = e.target.value; }}/>
                      </Col>
                      <Col md="6">
                        <label style={{fontSize: 12, fontWeight: 600}}>제목</label>
                        <FormInput value={this.emailSubject} onChange={e => { this.emailSubject = e.target.value; }} disabled
                          title="제목은 백엔드 메일 템플릿에 의해 자동 결정됩니다"/>
                      </Col>
                    </Row>
                    <div className="mt-2">
                      <label style={{fontSize: 12, fontWeight: 600}}>본문 미리보기</label>
                      <div style={{border: '1px solid #E5E7EB', borderRadius: 4, padding: 8, background: '#FAFAFA'}}>
                        <iframe srcDoc={this.emailHtml} style={{width: '100%', height: 560, border: 0, background: '#fff', display: 'block'}} title="email-preview"/>
                      </div>
                      <small style={{color: '#9CA3AF'}}>이메일 본문은 서버 템플릿 그대로 발송됩니다 (현 단계에서는 편집 불가).</small>
                    </div>
                    <div style={{marginTop: 10, padding: '8px 10px', background: this.confirmEmail ? '#ECFDF5' : '#FEF3C7', border: `1px solid ${this.confirmEmail ? '#A7F3D0' : '#FDE68A'}`, borderRadius: 4}}>
                      <FormCheckbox checked={this.confirmEmail} onChange={() => { this.confirmEmail = !this.confirmEmail; }}>
                        <span style={{fontSize: 13, fontWeight: 600, color: this.confirmEmail ? '#065F46' : '#92400E'}}>
                          이메일 내용을 확인했고, 발송에 동의합니다.
                        </span>
                      </FormCheckbox>
                    </div>
                  </>
                )}
              </div>

              {/* === 문자 === */}
              {(() => {
                const smsDisabled = !p.sms.isMobile;
                const smsDisabledReason = !p.sms.to
                  ? '등록된 전화번호가 없습니다'
                  : '등록된 번호가 모바일 형식이 아닙니다';
                return (
              <div style={{
                border: '1px solid #E5E7EB',
                borderRadius: 6,
                padding: 12,
                background: smsDisabled ? '#F3F4F6' : (this.sendSms ? '#FFFFFF' : '#F9FAFB'),
                opacity: smsDisabled ? 0.7 : 1,
              }}>
                <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10}}>
                  <label style={{display: 'inline-flex', alignItems: 'center', gap: 6, margin: 0, cursor: smsDisabled ? 'not-allowed' : 'pointer'}}>
                    <input type="checkbox" disabled={smsDisabled}
                      checked={!smsDisabled && this.sendSms}
                      onChange={() => { if (!smsDisabled) this.sendSms = !this.sendSms; }}/>
                    <strong style={{color: smsDisabled ? '#9CA3AF' : 'inherit'}}>문자 발송 (알리고 SMS)</strong>
                  </label>
                  {smsDisabled && (
                    <span style={{fontSize: 12, color: '#DC2626', fontWeight: 600}}>※ {smsDisabledReason} — 발송 불가</span>
                  )}
                  {this.result.sms && (
                    <span style={{marginLeft: 'auto', fontSize: 12, color: this.result.sms.ok ? '#10B981' : '#DC2626'}}>
                      {this.result.sms.ok ? '✓ 발송 완료' : '✕ ' + this.result.sms.error}
                    </span>
                  )}
                </div>
                {!smsDisabled && this.sendSms && (
                  <>
                    <Row>
                      <Col md="4">
                        <label style={{fontSize: 12, fontWeight: 600}}>발신번호</label>
                        <FormInput value={this.smsSender} onChange={e => { this.smsSender = e.target.value; }}/>
                      </Col>
                      <Col md="4">
                        <label style={{fontSize: 12, fontWeight: 600}}>수신번호</label>
                        <FormInput value={this.smsTo} onChange={e => { this.smsTo = e.target.value; }}/>
                      </Col>
                      <Col md="4">
                        <label style={{fontSize: 12, fontWeight: 600}}>메시지 타입</label>
                        <select className="form-control custom-select" value={this.smsMsgType}
                          onChange={e => { this.smsMsgType = e.target.value; }}>
                          <option value="AUTO">자동 ({this.smsAutoType})</option>
                          <option value="SMS">SMS (단문)</option>
                          <option value="LMS">LMS (장문)</option>
                        </select>
                      </Col>
                    </Row>
                    <div className="mt-2">
                      <label style={{fontSize: 12, fontWeight: 600}}>문자 내용</label>
                      <FormTextarea rows="5" value={this.smsMessage}
                        onChange={e => { this.smsMessage = e.target.value; }}/>
                      <div style={{textAlign: 'right', fontSize: 11, color: this.smsLength > 90 ? '#D97706' : '#6B7280'}}>
                        {this.smsLength}자 / {this.smsAutoType}
                      </div>
                    </div>
                    <div style={{marginTop: 10, padding: '8px 10px', background: this.confirmSms ? '#ECFDF5' : '#FEF3C7', border: `1px solid ${this.confirmSms ? '#A7F3D0' : '#FDE68A'}`, borderRadius: 4}}>
                      <FormCheckbox checked={this.confirmSms} onChange={() => { this.confirmSms = !this.confirmSms; }}>
                        <span style={{fontSize: 13, fontWeight: 600, color: this.confirmSms ? '#065F46' : '#92400E'}}>
                          문자 내용을 확인했고, 발송에 동의합니다.
                        </span>
                      </FormCheckbox>
                    </div>
                  </>
                )}
              </div>
                );
              })()}

              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 14, padding: '12px 0', borderTop: '1px solid #E5E7EB'}}>
                <div style={{fontSize: 12, color: '#6B7280'}}>
                  {!this.sendEmail && !this.sendSms && <span style={{color: '#DC2626'}}>이메일 또는 문자를 1개 이상 선택하세요.</span>}
                  {this.sendEmail && !this.confirmEmail && <span style={{color: '#D97706'}}>· 이메일 확인 필요</span>}
                  {this.sendSms && !this.confirmSms && <span style={{color: '#D97706', marginLeft: 6}}>· 문자 확인 필요</span>}
                  {this.canSubmit && <span style={{color: '#10B981'}}><FeatherIcon icon="check" size="12" style={{verticalAlign: '-2px'}}/> 발송 준비 완료</span>}
                </div>
                <div style={{display: 'flex', gap: 8}}>
                  <Button theme="light" size="sm" onClick={() => this.close()} disabled={this.isSending}>
                    닫기
                  </Button>
                  <Button theme="primary" size="sm" disabled={!this.canSubmit}
                    onClick={() => this.onSubmit()}>
                    <FeatherIcon icon="send" size="13" style={{verticalAlign: '-2px', marginRight: 4}}/>
                    {this.isSending ? '발송 중…' : '검토 후 발송'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </ModalBody>
      </Modal>
      </>
    );
  }
}

export default NotificationSendModal;
